import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Briefcase, DollarSign, Users, TrendingUp, Clock, Download, AlertTriangle, Building2, Target, Activity } from 'lucide-react';
import { IntelligenceLayout, MetricCard, fetchWithAuth, CHART_COLORS } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FunnelBar = ({ data }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="space-y-3">
      {data.map((item) => (
        <div key={item.name}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-700">{item.name}</span>
            <span className="text-sm font-bold text-[#0A1F44]">{item.value}</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-7 relative overflow-hidden">
            <div className="h-full rounded-full flex items-center transition-all duration-700"
              style={{ width: `${Math.max((item.value / max) * 100, 4)}%`, backgroundColor: item.color }}>
            </div>
            {item.rate && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500">{item.rate}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const IntelligenceOperations = () => {
  const [pipeline, setPipeline] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [clientActivity, setClientActivity] = useState(null);
  const [agingSearches, setAgingSearches] = useState(null);
  const [sourceEffectiveness, setSourceEffectiveness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    Promise.all([
      fetchWithAuth(`${API_URL}/api/operations/pipeline`),
      fetchWithAuth(`${API_URL}/api/operations/revenue`),
      fetchWithAuth(`${API_URL}/api/operations/funnel`),
      fetchWithAuth(`${API_URL}/api/operations/client-activity`),
      fetchWithAuth(`${API_URL}/api/operations/aging-searches`),
      fetchWithAuth(`${API_URL}/api/operations/source-effectiveness`),
    ]).then(([p, r, f, ca, as2, se]) => {
      setPipeline(p); setRevenue(r); setFunnel(f);
      setClientActivity(ca); setAgingSearches(as2); setSourceEffectiveness(se);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleExportExcel = () => {
    window.open(`${API_URL}/api/operations/export/excel`, '_blank');
  };

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'clients', label: 'Client Activity' },
    { key: 'aging', label: 'Aging Searches' },
    { key: 'sources', label: 'Source Effectiveness' },
  ];

  return (
    <IntelligenceLayout title="Operations">
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]">Agency Operations</h1>
            <p className="text-sm text-gray-500">Pipeline health, revenue tracking, and recruiting performance</p>
          </div>
          <button onClick={handleExportExcel} data-testid="export-excel-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm bg-[#D4AF37] text-[#0A1F44] rounded-lg hover:bg-[#c9a432] font-medium transition-colors">
            <Download className="h-4 w-4" /> Export Excel Report
          </button>
        </div>
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm rounded-t-lg transition-colors ${
                activeTab === tab.key ? 'bg-[#f0f4f8] text-[#0A1F44] font-medium border-t border-x border-gray-200' : 'text-gray-500 hover:text-[#0A1F44] hover:bg-gray-100'
              }`}>{tab.label}</button>
          ))}
        </div>
      </header>

      <main className="p-8">
        {loading ? (
          <div className="text-center py-20 text-gray-400">
            <Activity className="h-8 w-8 animate-pulse mx-auto mb-3" />
            <p>Loading operations data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Pipeline Health</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="pipeline-cards">
                    <MetricCard label="Active Searches" value={pipeline?.active_searches || 0} icon={Briefcase} />
                    <MetricCard label="Candidates Submitted" value={pipeline?.candidates_submitted || 0} icon={Users} />
                    <MetricCard label="Interviews" value={pipeline?.interviews_scheduled || 0} icon={Activity} />
                    <MetricCard label="Offers Pending" value={pipeline?.offers_pending || 0} icon={Target} />
                    <MetricCard label="Placements (Month)" value={pipeline?.placements_this_month || 0} icon={TrendingUp} />
                    <MetricCard label="Total Candidates" value={pipeline?.total_candidates || 0} icon={Users} />
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Revenue Metrics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="revenue-cards">
                    <MetricCard label="Collected Revenue" value={`$${(revenue?.collected_revenue || 0).toLocaleString()}`} icon={DollarSign} />
                    <MetricCard label="Projected Revenue" value={`$${(revenue?.projected_revenue || 0).toLocaleString()}`} icon={TrendingUp} />
                    <MetricCard label="Pipeline Value" value={`$${(revenue?.pipeline_value || 0).toLocaleString()}`} icon={Briefcase} />
                    <MetricCard label="Avg Placement Fee" value={`$${(revenue?.avg_placement_fee || 0).toLocaleString()}`} icon={DollarSign} />
                    <MetricCard label="Total Placements" value={revenue?.total_placements || 0} icon={Target} />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="recruiting-funnel">
                    <h3 className="text-sm font-semibold text-[#0A1F44] mb-1 uppercase tracking-wider">Recruiting Funnel</h3>
                    <p className="text-xs text-gray-400 mb-4">{funnel?.period || 'Last 90 Days'}</p>
                    <FunnelBar data={[
                      { name: 'Submissions', value: funnel?.submissions || pipeline?.candidates_submitted || 0, color: '#2563eb' },
                      { name: 'Interviews', value: funnel?.interviews || pipeline?.interviews_scheduled || 0, color: '#7c3aed', rate: funnel?.conversion_rates?.submission_to_interview ? `${funnel.conversion_rates.submission_to_interview}%` : '' },
                      { name: 'Offers', value: funnel?.offers || pipeline?.offers_pending || 0, color: '#d97706', rate: funnel?.conversion_rates?.interview_to_offer ? `${funnel.conversion_rates.interview_to_offer}%` : '' },
                      { name: 'Placements', value: funnel?.placements || pipeline?.placements_this_month || 0, color: '#059669', rate: funnel?.conversion_rates?.offer_to_placement ? `${funnel.conversion_rates.offer_to_placement}%` : '' },
                    ]} />
                    {funnel?.conversion_rates?.submission_to_placement !== undefined && (
                      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-xs text-gray-500">Overall Conversion Rate</span>
                        <span className="text-lg font-bold text-[#D4AF37]">{funnel.conversion_rates.submission_to_placement}%</span>
                      </div>
                    )}
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="client-revenue-chart">
                    <h3 className="text-sm font-semibold text-[#0A1F44] mb-4 uppercase tracking-wider">Revenue by Client</h3>
                    {clientActivity?.clients?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={clientActivity.clients.filter(c => c.revenue > 0).slice(0, 8)} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                          <YAxis type="category" dataKey="client" width={160} tick={{ fill: '#374151', fontSize: 10 }} />
                          <Tooltip content={({ active, payload }) => active && payload?.length ? (
                            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm shadow-lg">
                              <p className="text-[#0A1F44] font-medium">{payload[0].payload.client}</p>
                              <p className="text-[#D4AF37] font-semibold">${payload[0].value.toLocaleString()}</p>
                              <p className="text-gray-500">{payload[0].payload.placements} placements</p>
                            </div>
                          ) : null} />
                          <Bar dataKey="revenue" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12 text-gray-400 text-sm">No revenue data yet</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'clients' && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="client-activity-table">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider">Client Activity Report</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3">Client</th>
                        <th className="text-right px-5 py-3">Searches</th>
                        <th className="text-right px-5 py-3">Placements</th>
                        <th className="text-right px-5 py-3">Revenue</th>
                        <th className="text-right px-5 py-3">Conversion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(clientActivity?.clients || []).map((c, i) => (
                        <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-[#0A1F44] font-medium">{c.client}</td>
                          <td className="px-5 py-3 text-right text-gray-600">{c.searches}</td>
                          <td className="px-5 py-3 text-right text-gray-600">{c.placements}</td>
                          <td className="px-5 py-3 text-right text-[#D4AF37] font-medium">${c.revenue.toLocaleString()}</td>
                          <td className="px-5 py-3 text-right text-gray-500">{c.searches > 0 ? Math.round((c.placements / c.searches) * 100) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                    {clientActivity?.clients?.length > 0 && (
                      <tfoot>
                        <tr className="border-t-2 border-gray-200 bg-gray-50">
                          <td className="px-5 py-3 text-[#0A1F44] font-bold">Total</td>
                          <td className="px-5 py-3 text-right text-[#0A1F44] font-bold">{clientActivity.clients.reduce((s, c) => s + c.searches, 0)}</td>
                          <td className="px-5 py-3 text-right text-[#0A1F44] font-bold">{clientActivity.clients.reduce((s, c) => s + c.placements, 0)}</td>
                          <td className="px-5 py-3 text-right text-[#D4AF37] font-bold">${clientActivity.clients.reduce((s, c) => s + c.revenue, 0).toLocaleString()}</td>
                          <td className="px-5 py-3"></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'aging' && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="aging-searches-table">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider">Aging Searches (30+ Days Open)</h3>
                  <span className="text-xs text-gray-400">{agingSearches?.total || 0} searches</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3">Role</th>
                        <th className="text-left px-5 py-3">Client</th>
                        <th className="text-right px-5 py-3">Days Open</th>
                        <th className="text-center px-5 py-3">Status</th>
                        <th className="text-center px-5 py-3">Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(agingSearches?.searches || []).length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-12 text-gray-400">
                          No searches open 30+ days. Your pipeline is fresh.
                        </td></tr>
                      ) : agingSearches.searches.map((s, i) => (
                        <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-[#0A1F44] font-medium">{s.role}</td>
                          <td className="px-5 py-3 text-gray-600">{s.client}</td>
                          <td className="px-5 py-3 text-right text-gray-600">{s.days_open}</td>
                          <td className="px-5 py-3 text-center">
                            <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 text-gray-500 text-xs rounded">{s.status}</span>
                          </td>
                          <td className="px-5 py-3 text-center">
                            <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                              s.days_open >= 60 ? 'bg-red-100 text-red-600' :
                              s.days_open >= 45 ? 'bg-amber-100 text-amber-700' :
                              'bg-yellow-50 text-yellow-700'
                            }`}>{s.days_open >= 60 ? 'High' : s.days_open >= 45 ? 'Medium' : 'Watch'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'sources' && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="source-effectiveness-table">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider">Candidate Source Effectiveness</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3">Source</th>
                        <th className="text-right px-5 py-3">Submissions</th>
                        <th className="text-right px-5 py-3">Interviews</th>
                        <th className="text-right px-5 py-3">Placements</th>
                        <th className="text-right px-5 py-3">Conversion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sourceEffectiveness?.sources || []).length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-12 text-gray-400">
                          No source data available yet. Data will appear as candidates are tracked.
                        </td></tr>
                      ) : sourceEffectiveness.sources.map((s, i) => (
                        <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-[#0A1F44] font-medium">{s.source}</td>
                          <td className="px-5 py-3 text-right text-gray-600">{s.submissions}</td>
                          <td className="px-5 py-3 text-right text-gray-600">{s.interviews}</td>
                          <td className="px-5 py-3 text-right text-gray-600">{s.placements}</td>
                          <td className="px-5 py-3 text-right">
                            <span className={`font-medium ${s.submissions > 0 && s.placements > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                              {s.submissions > 0 ? Math.round((s.placements / s.submissions) * 100) : 0}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default IntelligenceOperations;
